import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { getUserData } from '../../../lib/utils';
import { useUserStore } from '../../../lib/stores/userStore';
import { useSettingsStore } from '../../../lib/stores/settingsStore';
import { AlfaDocsService } from '../../../lib/services/alfadocsService';
import { fetchReferentiContacts } from '../../../lib/utils/fetchReferentiContacts';
import { useGoogleCalendar } from '../../../hooks/deals/useGoogleCalendar';
import { resolveUserSyncSettings } from './syncSettings';
import { TYPES_WITH_ATTENDEES } from './eventData';
import type { ResolvedSyncSettings } from './types';
import type { AttendeeEntry } from './AttendeesPicker';

/** Utente CRM come serve al drawer: opzioni attendee + scope Google per l'organizer. */
export interface DrawerUserOption {
  id: string;
  email: string | null;
  full_name?: string | null;
  google_workspace_scopes?: string[] | null;
}

/** Gruppo di opzioni extra per la select "Tipo" (oggi solo AlfaDocs). */
export interface ExtraTypeGroup {
  label: string;
  options: { value: string; label: string }[];
}

export interface DrawerData {
  currentUserId: string | null;
  organizationId: string | null;
  users: DrawerUserOption[];
  /** Sync Google/Microsoft dell'ASSEGNATARIO (ricaricate ad ogni cambio assegnatario). */
  settings: ResolvedSyncSettings | null;
  teamsServiceEnabled: boolean;
  client: { name: string | null; phone: string | null; email: string | null };
  availableContacts: AttendeeEntry[];
  /**
   * Attendee da PRE-SELEZIONARE: contatti del cliente per una nuova attività, oppure
   * gli invitati reali letti da Google Calendar in edit. `null` finché non risolti.
   * Nuova identità array ad ogni risoluzione → la shell la applica con un effect.
   */
  resolvedAttendees: AttendeeEntry[] | null;
  alfaDocsTypeGroup: ExtraTypeGroup | null;
  activityDefaultType: string;
  activityTitleRequired: boolean;
}

const DEFAULT_ALFADOCS_TYPES = [
  { value: 'visita', label: 'Visita studio', enabled: true },
  { value: 'operazione', label: 'Operazione', enabled: true },
  { value: 'telehealth', label: 'Telehealth', enabled: true },
];

/** Fonte: LeadStatusUpdateDrawer.tsx:1974-1989 (opzioni `alfadocs_*` nella select Tipo). */
function buildAlfaDocsTypeGroup(config: any): ExtraTypeGroup | null {
  const apptConfig = config?.configuration?.appointments_config;
  const types = (apptConfig?.types || DEFAULT_ALFADOCS_TYPES).filter((t: any) => t.enabled);
  if (types.length === 0) return null;
  return {
    label: apptConfig?.label || 'Appuntamento clinico',
    options: types.map((t: any) => ({ value: `alfadocs_${t.value}`, label: t.label })),
  };
}

/**
 * Caricamento dei dati di CONTORNO del drawer condiviso Attività/Nota — estratto dalla
 * shell (`ActivityNoteDrawer.tsx`) per tenerla a sola orchestrazione e senza query.
 * Solo LETTURE: nessuna scrittura vive qui (stanno nell'adapter e in `useActivitySave`).
 *
 * Fonte: LeadStatusUpdateDrawer.tsx:424-445 (load su apertura: utenti, sync settings,
 * dati cliente, config AlfaDocs), :453-487/:519-530 (sync settings dell'assegnatario,
 * ricaricate al cambio assegnatario), :614-731 (contatti + attendee per i tipi con
 * invitati, incluso il caricamento degli attendee reali dall'evento Google in edit).
 */
export function useDrawerData(params: {
  isOpen: boolean;
  clientId?: string;
  assigneeId: string | null;
  activityType: string;
  editingGoogleEventId?: string | null;
  editingCalendarId?: string | null;
}): DrawerData {
  const { isOpen, clientId, assigneeId, activityType, editingGoogleEventId, editingCalendarId } = params;

  const { users, fetchUsers } = useUserStore();
  const { settings: orgSettings } = useSettingsStore();
  const { getEvent } = useGoogleCalendar();

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [settings, setSettings] = useState<ResolvedSyncSettings | null>(null);
  const [client, setClient] = useState<DrawerData['client']>({ name: null, phone: null, email: null });
  const [availableContacts, setAvailableContacts] = useState<AttendeeEntry[]>([]);
  const [resolvedAttendees, setResolvedAttendees] = useState<AttendeeEntry[] | null>(null);
  const [alfaDocsTypeGroup, setAlfaDocsTypeGroup] = useState<ExtraTypeGroup | null>(null);
  // Fonte :620-624/:734-738 — evita doppio caricamento simultaneo degli attendee.
  const loadingAttendeesRef = useRef(false);

  // === Apertura: utenti, identità/org, config AlfaDocs (fonte :424-445) ===
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    fetchUsers(false);
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        if (!cancelled) setCurrentUserId(data.user?.id ?? null);
      } catch {
        /* ignore */
      }
      try {
        const userData = await getUserData();
        if (cancelled || !userData?.organizationId) return;
        setOrganizationId(userData.organizationId);
        const oldest = await AlfaDocsService.getOldestActiveConfig(userData.organizationId);
        if (!cancelled) setAlfaDocsTypeGroup(oldest ? buildAlfaDocsTypeGroup(oldest) : null);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, fetchUsers]);

  // === Dati cliente per la descrizione "enhanced" dell'evento (fonte :308-338, :386-394) ===
  useEffect(() => {
    if (!isOpen || !clientId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('clients')
        .select('business_name, trade_name, contact_name, email, email_amministrazione, email_legale_rappresentante, phone, whatsapp_number')
        .eq('id', clientId)
        .single();
      if (cancelled || !data) return;
      const name = `${data.business_name || data.trade_name || ''} ${data.contact_name ? `(${data.contact_name})` : ''}`.trim();
      setClient({
        name: name || null,
        phone: data.phone || data.whatsapp_number || null,
        email: data.email || data.email_amministrazione || data.email_legale_rappresentante || null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, clientId]);

  // === Sync settings dell'assegnatario (fonte :519-530) ===
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const resolved = await resolveUserSyncSettings(assigneeId || currentUserId);
      if (!cancelled) setSettings(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, assigneeId, currentUserId]);

  // === Contatti + attendee da pre-selezionare (fonte :614-731) ===
  useEffect(() => {
    if (!isOpen) {
      loadingAttendeesRef.current = false;
      return;
    }
    if (!(TYPES_WITH_ATTENDEES as readonly string[]).includes(activityType) || !clientId) {
      setAvailableContacts([]);
      setResolvedAttendees([]);
      return;
    }
    if (loadingAttendeesRef.current) return;
    loadingAttendeesRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { data: clientRow } = await supabase.from('clients').select('email, business_name').eq('id', clientId).single();
        const contacts: AttendeeEntry[] = [];
        if (clientRow?.email) contacts.push({ email: clientRow.email, name: clientRow.business_name || undefined });
        const referenti = await fetchReferentiContacts(clientId);
        referenti.forEach((c) => {
          if (c.email) contacts.push({ email: c.email, name: c.name || undefined });
        });
        if (cancelled) return;
        setAvailableContacts(contacts);

        // Edit di un'attività già a calendario: gli invitati reali stanno sull'evento
        // Google, non fra i contatti del cliente (fonte :667-712, fallback ai contatti).
        const eventId = editingGoogleEventId?.trim();
        const calendarId = editingCalendarId?.trim();
        if (eventId && calendarId) {
          try {
            const googleEvent = await getEvent(eventId, calendarId);
            const googleAttendees: AttendeeEntry[] = (googleEvent?.attendees || [])
              .filter((att: any) => att.email)
              .map((att: any) => ({ email: att.email, name: att.displayName }));
            if (!cancelled) setResolvedAttendees(googleAttendees.length > 0 ? googleAttendees : contacts);
          } catch (error) {
            console.error('[useDrawerData] errore lettura attendee da Google Calendar:', error);
            if (!cancelled) setResolvedAttendees(contacts);
          }
        } else if (!cancelled) {
          setResolvedAttendees(contacts);
        }
      } catch (error) {
        console.error('[useDrawerData] errore caricamento contatti attendee:', error);
      } finally {
        loadingAttendeesRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
    // `getEvent` volutamente fuori dalle deps (identità non stabile) — come la fonte :731.
  }, [isOpen, clientId, activityType, editingGoogleEventId, editingCalendarId]);

  return {
    currentUserId,
    organizationId,
    users: users as unknown as DrawerUserOption[],
    settings,
    teamsServiceEnabled: !!(orgSettings as any)?.microsoft_sync_teams,
    client,
    availableContacts,
    resolvedAttendees,
    alfaDocsTypeGroup,
    activityDefaultType: orgSettings?.activity_default_type || 'task',
    activityTitleRequired: orgSettings?.activity_title_required !== false,
  };
}
